import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "../data/site";
import { useContent, useSettings } from "../hooks/useCms";
import { resolveImage } from "../data/site";
import { FadeUp } from "../components/motion";
import CyclingImage from "../components/CyclingImage";
import PageHeader from "../components/PageHeader";
import EditorialMarquee from "../components/EditorialMarquee";
import SEO from "../components/SEO";

const DEFAULT_BIO = `This is your builder.

Mark Roberts brings a rare combination of construction expertise, business knowledge, creativity, and hands-on experience to every home he builds. With degrees in Construction and Business Management and more than 10 years of experience in the industry, Mark understands the building process from the ground up—and knows how to turn an idea into something real.

Bring him your ideas, your needs, your inspiration, or even something that's never been built before. Mark thrives on finding ways to make ambitious ideas possible. From thoughtfully designed homes, hidden rooms, unique architectural features, and highly complex custom details, Mark approaches every project with the skill, ingenuity, and attention to detail it deserves.

He is meticulous. Thorough. Relentlessly detail-oriented. For Mark, "good enough" simply isn't good enough. There's one standard that never changes: If his name is on it, it has to be done right.

Mark is from the Treasure Valley. He believes in the life people come here to build—the space, the freedom, the land, the community, and the opportunity to create something that is truly their own.

When you work with Mark, you're not just hiring a builder. You're working directly with someone who is personally invested in your home, your experience, and the finished product. Because when it's your home, it should matter to your builder just as much as it matters to you.`;

const VALUES = ["Personal", "Premium", "Quality", "Thoughtful", "Trustworthy", "Idaho", "Craftsmanship"];

const About = () => {
  const c = useContent();
  const settings = useSettings();
  const bioParagraphs = c("about_bio", DEFAULT_BIO).split("\n\n").filter(Boolean);
  return (
  <main>
    <SEO />
    <PageHeader
      testid="about-header"
      eyebrow="About On Point Homes"
      lines={[{ text: "Small On Purpose." }, { text: "Serious About Homes.", italic: true }]}
      description="On Point Homes exists for one reason: to build homes the way they should be built — with intention, attention, and a builder who's personally invested in every one."
    />

    <section className="pb-24 md:pb-36">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <FadeUp className="lg:col-span-6">
          <p className="eyebrow text-[#8C6A4F] mb-6">The Brand</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">Why "On Point"?</h2>
          <p className="mt-8 text-[#5C5A56] leading-relaxed text-base md:text-lg">
            On point means exactly right — precise, dialed in, no compromises. It's how we think a home
            should be built and how we think a builder should operate. The dog in our logo isn't an
            accident either: loyalty, watchfulness, and always being by your side. That's the kind of
            builder we set out to be.
          </p>
          <p className="mt-5 text-[#5C5A56] leading-relaxed text-base md:text-lg">
            We deliberately stay small. Fewer projects means every home gets walked, checked, and
            obsessed over by the person whose name is on the sign. When you call On Point Homes,
            you talk to the builder.
          </p>
        </FadeUp>
        <FadeUp className="lg:col-span-6 img-zoom relative aspect-[4/3]" delay={0.15}>
          <CyclingImage start={27} interval={5500} alt="Home built by On Point Homes — Idaho home builder brand" />
        </FadeUp>
      </div>
    </section>

    <EditorialMarquee items={VALUES} />

    <section className="py-24 md:py-36 bg-[#EFECE7]">
      <div className="max-w-[1600px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <FadeUp className="lg:col-span-5 img-zoom relative aspect-[4/3]">
          <CyclingImage start={14} interval={5900} alt="Craftsmanship inside an On Point Homes build" />
        </FadeUp>
        <FadeUp className="lg:col-span-6 lg:col-start-7" delay={0.15}>
          <p className="eyebrow text-[#8C6A4F] mb-6">The Builder</p>
          <div className="flex items-center gap-6">
            <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 overflow-hidden bg-[#1A1A1A] flex items-center justify-center" data-testid="builder-photo">
              {settings.builder_photo ? (
                <img src={resolveImage(settings.builder_photo)} alt="Mark Roberts — On Point Homes builder" className="w-full h-full object-cover" />
              ) : (
                <span className="font-serif text-3xl md:text-4xl text-[#8C6A4F]">MR</span>
              )}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight">{c("about_headline", "Meet Mark Roberts")}</h2>
          </div>
          {bioParagraphs.map((p, i) => (
            <p key={i} className={i === 0 ? "mt-8 text-[#1A1A1A] leading-relaxed text-base md:text-lg font-normal" : "mt-5 text-[#5C5A56] leading-relaxed text-base md:text-lg"}>
              {p}
            </p>
          ))}
          <Link to="/contact" data-testid="about-cta-btn" className="mt-10 inline-flex items-center gap-3 bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-8 py-4 btn-sweep">
            Contact Your Builder <ArrowRight size={14} />
          </Link>
        </FadeUp>
      </div>
    </section>
  </main>
  );
};

export default About;
